import { useState, useCallback } from 'react';
import { formatMessage } from 'devextreme/localization';

export interface DeleteResult<U> {
    success: boolean;
    message?: string;
    result?: U | null;
}

interface useDeleteConfirmationReturn<T,U> {
    isVisible: boolean;
    itemToDelete: T | null;
    showConfirm: (item: T) => void;
    handleConfirm: () => Promise<DeleteResult<U>>;
    handleCancel: () => void;
}

export const useDeleteConfirmation = <T,U, >(
    onDelete: (item: T) => Promise<DeleteResult<U>>,
    onTerminate: (item: T, result: DeleteResult<U>) => void
): useDeleteConfirmationReturn<T, U> => {
    const [isVisible, setIsVisible] = useState<boolean>(false);
    const [itemToDelete, setItemToDelete] = useState<T | null>(null);

    const showConfirm = useCallback((item: T): void => {
        setItemToDelete(item);
        setIsVisible(true);
    }, []);

    const handleConfirm = useCallback(async (): Promise<DeleteResult<U>> => {
        if (!itemToDelete) {
            return { success: false, message: formatMessage("DeleteConfirmationForm_NoElement"), result: null };
        }

        try {
            const result = await onDelete(itemToDelete);

            await onTerminate(itemToDelete, result);

            if (result.success) {
                setItemToDelete(null);
            }

            return result;
        } catch (error) {
            return {
                success: false,
                message: error instanceof Error ? error.message : formatMessage("DeleteConfirmationForm_UnknownError")
            };
        } finally {
            setIsVisible(false);
        }
    }, [itemToDelete, onDelete, onTerminate]);

    const handleCancel = useCallback((): void => {
        setIsVisible(false);
        setItemToDelete(null);
    }, []);

    return {
        isVisible,
        itemToDelete,
        showConfirm,
        handleConfirm,
        handleCancel
    };
};